import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ChevronDown, Loader2, AlertTriangle, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../Components/Feedback';
import { Card } from '../../Components/ui';
import StatFillIcon, { STAT_ICON_PATHS } from '../../Components/StatFillIcon';
import ComparisonBar, { corInstituicao } from '../../Components/ComparisonBar';
import { AREAS } from '../../utils/areas';
import { API_URL } from '../../api/config';
import GraficoTendenciaInscritos from './GraficoTendenciaInscritos';

// Só master/coordenador (ver perfis={['master','coordenador']} em App.js e
// Layout.jsx) — compara dados GERAIS (estrutura: matrícula, professor, turma;
// não frequência dia-a-dia) de TODAS as instituições que o usuário logado tem
// acesso de uma vez, não só a selecionada no seletor de instituição (por isso
// não usa useInstitution/x-institution-id — ver backend/estatisticas-comparativas.js).
// Se o usuário só tem acesso a 1 instituição, mostra as estatísticas dela
// direto, sem forçar uma "comparação" com um lado só.
const TURNOS_ORDEM = [
  { chave: 'manha', label: 'Manhã' },
  { chave: 'tarde', label: 'Tarde' },
  { chave: 'noite', label: 'Noite' },
  { chave: 'outro', label: 'Outro' },
];

// Cores FIXAS por sexo (não pela paleta de instituição) — aqui o que importa
// comparar é M x F x não informado dentro de cada instituição/nível, não uma
// instituição contra a outra.
const COR_SEXO = {
  M: { light: '#93C5FD', dark: '#2563EB', label: 'Masculino' },
  F: { light: '#F9A8D4', dark: '#DB2777', label: 'Feminino' },
  outro: { light: '#D1D5DB', dark: '#6B7280', label: 'Não informado' },
};

// Ordena "1","2","3","4" numericamente crescente, com "sem_nivel" sempre por
// último (não é um nível "maior", é a ausência de um).
const ordenarChavesNivel = (chaves) =>
  [...chaves].sort((a, b) => {
    if (a === 'sem_nivel') return 1;
    if (b === 'sem_nivel') return -1;
    return Number(a) - Number(b);
  });
const labelNivel = (chave) => (chave === 'sem_nivel' ? 'Sem Nível' : `Nível ${chave}`);

const NIVEIS_CAPACIDADE = ['1', '2', '3', '4'];
const TURNOS_CAPACIDADE = [
  { chave: 'manha', label: 'Manhã' },
  { chave: 'tarde', label: 'Tarde' },
  { chave: 'noite', label: 'Noite' },
];
const QUERY_KEY = ['estatisticas-comparativas'];

function EstatisticasComparativas() {
  const { token } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [professoresAbertos, setProfessoresAbertos] = useState(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/estatisticas-comparativas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar comparativo.');
      return json?.data ?? json;
    },
    enabled: !!token,
  });

  const instituicoes = useMemo(() => data?.instituicoes ?? [], [data]);
  const comparando = instituicoes.length > 1;

  const cores = useMemo(() => instituicoes.map((_, i) => corInstituicao(i)), [instituicoes]);

  // Estado e dados do Gráfico de Tendências Anual
  const [anoTendencia, setAnoTendencia] = useState(() => new Date().getFullYear());
  const [idInstSelecionadaManual, setIdInstSelecionadaManual] = useState(null);

  // Instituição efetiva para o gráfico de tendências (a selecionada ou a primeira da lista)
  const idInstTendencia = idInstSelecionadaManual || instituicoes[0]?.id || null;

  const QUERY_KEY_TENDENCIA = ['tendencia-anual', idInstTendencia, anoTendencia];
  const { data: dadosTendencia, isLoading: loadingTendencia } = useQuery({
    queryKey: QUERY_KEY_TENDENCIA,
    queryFn: async () => {
      const url = `${API_URL}/api/estatisticas-comparativas/tendencia?ano=${anoTendencia}${idInstTendencia ? `&id_instituicao=${idInstTendencia}` : ''}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(idInstTendencia && { 'x-institution-id': String(idInstTendencia) }),
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar dados de tendência.');
      return json?.data ?? json;
    },
    enabled: !!token && !!idInstTendencia,
  });

  const salvarHistoricoManual = async (valores) => {
    try {
      const res = await fetch(`${API_URL}/api/estatisticas-comparativas/tendencia/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(idInstTendencia && { 'x-institution-id': String(idInstTendencia) }),
        },
        body: JSON.stringify({
          id_instituicao: idInstTendencia,
          ano: anoTendencia,
          valores,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao salvar histórico manual.');
      toast.success('Histórico manual de inscritos salvo com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['tendencia-anual'] });
    } catch (err) {
      toast.error(err.message);
      throw err;
    }
  };

  const salvarMeta = async (novaMeta) => {
    try {
      const res = await fetch(`${API_URL}/api/estatisticas-comparativas/tendencia/meta`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(idInstTendencia && { 'x-institution-id': String(idInstTendencia) }),
        },
        body: JSON.stringify({
          id_instituicao: idInstTendencia,
          ano: anoTendencia,
          meta: novaMeta,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao salvar meta.');
      toast.success('Meta de inscritos atualizada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['tendencia-anual'] });
    } catch (err) {
      toast.error(err.message);
      throw err;
    }
  };

  const toggleProfessores = (id) => {
    setProfessoresAbertos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Turnos e áreas que aparecem em pelo menos uma instituição — não mostra
  // seção vazia (ex.: nenhuma instituição tem turma da Noite ainda).
  const turnosVisiveis = TURNOS_ORDEM.filter((t) =>
    instituicoes.some((i) => (i.por_turno[t.chave] || 0) > 0),
  );
  const areasVisiveis = AREAS.filter((a) =>
    instituicoes.some((i) => (i.por_area[a.value] || 0) > 0),
  );
  const turnosCapacidadeVisiveis = TURNOS_CAPACIDADE.filter((t) =>
    instituicoes.some((i) =>
      NIVEIS_CAPACIDADE.some((n) => {
        const cel = i.capacidade?.[n]?.[t.chave];
        return cel && (cel.capacidade > 0 || cel.atual > 0);
      }),
    ),
  );

  // Atualiza a capacidade de UMA célula — grava no backend e, se der certo,
  // corrige só aquele número no cache do react-query (sem refetch: os outros
  // valores não mudam só por editar a capacidade de um nível/turno).
  const salvarCapacidade = async (idInstituicao, nivel, turno, novaCapacidade) => {
    try {
      const res = await fetch(`${API_URL}/api/estatisticas-comparativas/capacidade`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(idInstituicao && { 'x-institution-id': String(idInstituicao) }),
        },
        body: JSON.stringify({
          id_instituicao: idInstituicao,
          nivel,
          turno,
          capacidade: novaCapacidade,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao salvar capacidade.');
      queryClient.setQueryData(QUERY_KEY, (old) => {
        if (!old) return old;
        return {
          ...old,
          instituicoes: old.instituicoes.map((inst) => {
            if (inst.id !== idInstituicao) return inst;
            const nivelChave = String(nivel);
            const atual = inst.capacidade?.[nivelChave]?.[turno]?.atual || 0;
            return {
              ...inst,
              capacidade: {
                ...inst.capacidade,
                [nivelChave]: {
                  ...inst.capacidade?.[nivelChave],
                  [turno]: { capacidade: novaCapacidade, atual },
                },
              },
            };
          }),
        };
      });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const maxDe = (campo) => Math.max(1, ...instituicoes.map((i) => i[campo] || 0));
  const maxTurno = (chave) => Math.max(1, ...instituicoes.map((i) => i.por_turno[chave] || 0));
  const maxArea = (valor) => Math.max(1, ...instituicoes.map((i) => i.por_area[valor] || 0));

  const niveisVisiveis = ordenarChavesNivel([
    ...new Set(instituicoes.flatMap((i) => Object.keys(i.por_nivel || {}))),
  ]);

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-3 max-w-7xl mx-auto space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4 text-blue-600" /> PAINEL DO GESTOR - INH
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {comparando
              ? `Dados gerais das ${instituicoes.length} instituições que você tem acesso, lado a lado.`
              : 'Dados gerais da instituição que você tem acesso.'}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-3" /> Carregando...
          </div>
        ) : error ? (
          <Card>
            <div className="text-center py-8 text-red-500">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">{error.message}</p>
            </div>
          </Card>
        ) : instituicoes.length === 0 ? (
          <Card>
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
              <p className="text-sm">Nenhuma instituição vinculada a essa conta ainda.</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Legenda de cores por instituição — usada em todas as seções abaixo */}
            {comparando && (
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {instituicoes.map((inst, i) => (
                  <div
                    key={inst.id}
                    className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400"
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: cores[i].dark }}
                    />
                    <span translate="no">{inst.nome}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Gráfico de Tendências Anual de Inscritos (Barras com Linha) */}
            <GraficoTendenciaInscritos
              dados={dadosTendencia}
              ano={anoTendencia}
              onChangeAno={setAnoTendencia}
              instituicoes={instituicoes}
              idInstituicaoSelecionada={idInstTendencia}
              onChangeInstituicao={setIdInstSelecionadaManual}
              onSalvarManual={salvarHistoricoManual}
              onSalvarMeta={salvarMeta}
              podeEditar={true}
              carregando={loadingTendencia}
            />

            {/* Visão geral: um ícone (preenchido proporcional ao maior valor entre
                as instituições) por instituição, por métrica */}
            <Card padding="sm">
              <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Visão Geral
              </h3>
              <div className="space-y-3.5">
                <SecaoIcones
                  titulo="Alunos Ativos"
                  path={STAT_ICON_PATHS.alunos}
                  instituicoes={instituicoes}
                  cores={cores}
                  campo="total_alunos_ativos"
                  maxValor={maxDe('total_alunos_ativos')}
                />
                <SecaoIcones
                  titulo="Fila de Espera"
                  path={STAT_ICON_PATHS.filaEspera}
                  instituicoes={instituicoes}
                  cores={cores}
                  campo="total_fila_espera"
                  maxValor={maxDe('total_fila_espera')}
                />
                <SecaoIcones
                  titulo="Educadores Ativos"
                  path={STAT_ICON_PATHS.professores}
                  instituicoes={instituicoes}
                  cores={cores}
                  campo="total_professores_ativos"
                  maxValor={maxDe('total_professores_ativos')}
                />
                <SecaoIcones
                  titulo="Turmas Ativas"
                  path={STAT_ICON_PATHS.turmas}
                  instituicoes={instituicoes}
                  cores={cores}
                  campo="total_turmas_ativas"
                  maxValor={maxDe('total_turmas_ativas')}
                />
                <SecaoIcones
                  titulo="Frequência Média do Mês"
                  path={STAT_ICON_PATHS.frequencia}
                  instituicoes={instituicoes}
                  cores={cores}
                  campo="frequencia_media_mes"
                  maxValor={100}
                  sufixo="%"
                />
              </div>
            </Card>

            {/* Capacidade fixa por nível/turno — número que master/coordenador
                digita direto na célula (não vem de nenhuma outra tabela), com
                a % de ocupação comparando contra quem está matriculado agora.
                Ocupação > 100% fica destacada em vermelho. */}
            {turnosCapacidadeVisiveis.length > 0 && (
              <Card padding="none">
                <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                    Capacidade por Nível
                  </h3>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    Capacidade é um número fixo (edite clicando na célula) — a % compara com quem
                    está matriculado agora.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="text-xs border-collapse w-full">
                    <thead>
                      <tr>
                        <th
                          rowSpan={2}
                          className="sticky left-0 bg-white dark:bg-gray-900 px-2 py-1.5 text-left text-[11px] font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800"
                        >
                          Nível
                        </th>
                        {instituicoes.map((inst, i) => (
                          <th
                            key={inst.id}
                            colSpan={turnosCapacidadeVisiveis.length * 2}
                            className="px-2 py-1 text-center text-[11px] font-semibold text-gray-700 dark:text-gray-200 border-b border-l border-gray-100 dark:border-gray-800"
                            translate="no"
                          >
                            <span className="inline-flex items-center gap-1">
                              <span
                                className="w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ background: cores[i].dark }}
                              />
                              {inst.nome}
                            </span>
                          </th>
                        ))}
                      </tr>
                      <tr>
                        {instituicoes.map((inst) =>
                          turnosCapacidadeVisiveis.map((t, j) => (
                            <th
                              key={`${inst.id}-${t.chave}`}
                              colSpan={2}
                              className={`px-1.5 py-1 text-center text-[10px] font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 ${j === 0 ? 'border-l border-gray-100 dark:border-gray-800' : ''}`}
                            >
                              {t.label}
                            </th>
                          )),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {NIVEIS_CAPACIDADE.map((nivel) => (
                        <tr
                          key={nivel}
                          className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                        >
                          <td className="sticky left-0 bg-white dark:bg-gray-900 px-2 py-1.5 text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                            Nível {nivel}
                          </td>
                          {instituicoes.map((inst) =>
                            turnosCapacidadeVisiveis.map((t, j) => {
                              const cel = inst.capacidade?.[nivel]?.[t.chave] || {
                                capacidade: 0,
                                atual: 0,
                              };
                              return (
                                <React.Fragment key={`${inst.id}-${t.chave}`}>
                                  <td
                                    className={`px-1.5 py-1 text-center ${j === 0 ? 'border-l border-gray-100 dark:border-gray-800' : ''}`}
                                  >
                                    <CapacidadeInput
                                      value={cel.capacidade}
                                      onSave={(novoValor) =>
                                        salvarCapacidade(inst.id, nivel, t.chave, novoValor)
                                      }
                                    />
                                  </td>
                                  <td className="px-1.5 py-1 text-center">
                                    <PctOcupacao capacidade={cel.capacidade} atual={cel.atual} />
                                  </td>
                                </React.Fragment>
                              );
                            }),
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Matrículas por turno */}
            {turnosVisiveis.length > 0 && (
              <Card padding="sm">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
                  Matrículas por Turno
                </h3>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-3">
                  Conta a matrícula (turma), não o aluno único — quem estuda de manhã e à noite
                  entra nos dois turnos.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3.5">
                  {turnosVisiveis.map((turno) => (
                    <div key={turno.chave}>
                      <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        {turno.label}
                      </p>
                      <div className="space-y-1">
                        {instituicoes.map((inst, i) => (
                          <ComparisonBar
                            key={inst.id}
                            compact
                            label={inst.nome}
                            value={inst.por_turno[turno.chave] || 0}
                            maxValue={maxTurno(turno.chave)}
                            colorLight={cores[i].light}
                            colorDark={cores[i].dark}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Matrículas por área */}
            {areasVisiveis.length > 0 && (
              <Card padding="sm">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Matrículas por Área
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3.5">
                  {areasVisiveis.map((area) => (
                    <div key={area.value}>
                      <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        {area.label}
                      </p>
                      <div className="space-y-1">
                        {instituicoes.map((inst, i) => (
                          <ComparisonBar
                            key={inst.id}
                            compact
                            label={inst.nome}
                            value={inst.por_area[area.value] || 0}
                            maxValue={maxArea(area.value)}
                            colorLight={cores[i].light}
                            colorDark={cores[i].dark}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Alunos por nível, e dentro de cada nível a composição por sexo —
                cores fixas de sexo (não da instituição), pra comparar M x F x
                não informado independente de qual instituição é qual. */}
            {niveisVisiveis.length > 0 && (
              <Card padding="sm">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Alunos por Nível
                </h3>
                <div className="flex flex-wrap gap-x-3.5 gap-y-1 mb-3.5">
                  {Object.entries(COR_SEXO).map(([chave, c]) => (
                    <div
                      key={chave}
                      className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400"
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: c.dark }}
                      />
                      {c.label}
                    </div>
                  ))}
                </div>
                <div className="space-y-4">
                  {niveisVisiveis.map((nivelChave) => (
                    <div key={nivelChave}>
                      <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        {labelNivel(nivelChave)}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                        {instituicoes.map((inst, i) => {
                          const stats = inst.por_nivel[nivelChave];
                          if (!stats || stats.total === 0) return null;
                          return (
                            <div
                              key={inst.id}
                              className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-2"
                            >
                              <div
                                className="flex items-center gap-1 mb-1 text-[11px] font-medium text-gray-700 dark:text-gray-300"
                                translate="no"
                              >
                                <span
                                  className="w-2 h-2 rounded-full shrink-0"
                                  style={{ background: cores[i].dark }}
                                />
                                {inst.nome}
                                <span className="text-gray-400 dark:text-gray-500 font-normal">
                                  ({stats.total})
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                {['M', 'F', 'outro']
                                  .filter((s) => stats[s] > 0)
                                  .map((s) => (
                                    <ComparisonBar
                                      key={s}
                                      compact
                                      label={COR_SEXO[s].label}
                                      value={stats[s]}
                                      maxValue={stats.total}
                                      colorLight={COR_SEXO[s].light}
                                      colorDark={COR_SEXO[s].dark}
                                    />
                                  ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Alunos por professor, separado por instituição — cada instituição
                tem seu próprio conjunto de professores, então não faz sentido
                comparar barra a barra entre instituições aqui; em vez disso é
                uma lista por instituição (colapsável, ordenada por nome). */}
            <Card padding="none">
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                  Alunos por Educador
                </h3>
              </div>
              <div className="divide-y divide-gray-50 dark:divide-gray-800">
                {instituicoes.map((inst, i) => {
                  const aberto = professoresAbertos.has(inst.id);
                  const professoresOrdenados = [...inst.professores].sort((a, b) =>
                    a.nome.localeCompare(b.nome, 'pt-BR'),
                  );
                  const maxAluno = Math.max(1, ...professoresOrdenados.map((p) => p.total_alunos));
                  return (
                    <div key={inst.id}>
                      <button
                        onClick={() => toggleProfessores(inst.id)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60"
                      >
                        <span
                          className="flex items-center gap-1.5 text-xs font-medium text-gray-900 dark:text-gray-100"
                          translate="no"
                        >
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: cores[i].dark }}
                          />
                          {inst.nome}
                          <span className="text-[11px] font-normal text-gray-400 dark:text-gray-500">
                            ({professoresOrdenados.length} educador
                            {professoresOrdenados.length === 1 ? '' : 'es'})
                          </span>
                        </span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${aberto ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {aberto && (
                        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                          {professoresOrdenados.length === 0 ? (
                            <p className="text-xs text-gray-400 dark:text-gray-500 py-2">
                              Nenhum educador ativo cadastrado.
                            </p>
                          ) : (
                            professoresOrdenados.map((prof) => (
                              <ComparisonBar
                                key={prof.id}
                                compact
                                label={prof.nome}
                                value={prof.total_alunos}
                                maxValue={maxAluno}
                                colorLight={cores[i].light}
                                colorDark={cores[i].dark}
                              />
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

// Uma "métrica" de Visão Geral: grade com um StatFillIcon por instituição.
function SecaoIcones({ titulo, path, instituicoes, cores, campo, maxValor, sufixo = '' }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">{titulo}</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
        {instituicoes.map((inst, i) => {
          const valor = inst[campo] || 0;
          const pct = campo === 'frequencia_media_mes' ? valor : (valor / maxValor) * 100;
          return (
            <div
              key={inst.id}
              className="flex flex-col items-center bg-gray-50 dark:bg-gray-800/60 rounded-lg p-2"
            >
              <StatFillIcon
                path={path}
                percentage={pct}
                colorLight={cores[i].light}
                colorDark={cores[i].dark}
                size="w-10 h-10"
              />
              <p className="text-xs font-bold text-gray-900 dark:text-gray-100 mt-0.5">
                {valor}
                {sufixo}
              </p>
              <p
                className="text-[10px] text-gray-500 dark:text-gray-400 text-center truncate w-full"
                translate="no"
              >
                {inst.nome}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Campo numérico da tabela de Capacidade — edição local (não salva a cada
// tecla), grava no blur/Enter e só chama onSave se o valor realmente mudou.
function CapacidadeInput({ value, onSave }) {
  const [valor, setValor] = useState(String(value || 0));

  useEffect(() => {
    setValor(String(value || 0));
  }, [value]);

  const confirmar = () => {
    const n = Math.max(0, parseInt(valor, 10) || 0);
    setValor(String(n));
    if (n !== value) onSave(n);
  };

  return (
    <input
      type="number"
      min="0"
      inputMode="numeric"
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
      }}
      className="w-11 h-6 text-center text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
    />
  );
}

// Badge de % de ocupação — vermelho quando passa de 100% (mesmo destaque da
// planilha que originou essa seção), neutro caso contrário. Sem capacidade
// ainda cadastrada (0), mostra só um traço em vez de dividir por zero.
function PctOcupacao({ capacidade, atual }) {
  if (!capacidade) {
    return <span className="text-xs text-gray-300 dark:text-gray-700">—</span>;
  }
  const pct = Math.round((atual / capacidade) * 100);
  const estourou = pct > 100;
  return (
    <span
      className={`inline-block min-w-[2.25rem] px-1 py-0.5 rounded-md text-[11px] font-semibold ${
        estourou
          ? 'bg-red-500 text-white'
          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
      }`}
      title={`${atual} de ${capacidade} vagas`}
    >
      {pct}%
    </span>
  );
}

export default EstatisticasComparativas;
